import { z } from "zod";
import { HydratedCompanySchema } from "./companies";
import { LocationSchema } from "./locations";
import { PersonSchema } from "./people";
import {
  CustomFieldFilterSchema,
  CustomFieldsSchemaSchema,
  CustomFieldValuesSchema,
} from "./customFields";

export const OPPORTUNITY_STATUS_VALUES = [
  "INTAKE",
  "QUOTE_IN_PROGRESS",
  "SENT",
  "ACCEPTED",
  "LOST",
] as const;
export const OpportunityStatusSchema = z.enum(OPPORTUNITY_STATUS_VALUES);
export type OpportunityStatus = z.infer<typeof OpportunityStatusSchema>;
export const OpportunityStatusEnum = OpportunityStatusSchema.enum;
export const OPPORTUNITY_STATUS = OpportunityStatusSchema.enum;

export const OPPORTUNITY_STATUS_DISPLAY_NAMES = {
  [OPPORTUNITY_STATUS.INTAKE]: "Intake",
  [OPPORTUNITY_STATUS.QUOTE_IN_PROGRESS]: "Quote In Progress",
  [OPPORTUNITY_STATUS.SENT]: "Quote Completed",
  [OPPORTUNITY_STATUS.ACCEPTED]: "Won",
  [OPPORTUNITY_STATUS.LOST]: "Lost",
} as const satisfies Record<OpportunityStatus, string>;

export const IN_PROGRESS_STATUSES: readonly OpportunityStatus[] = [
  OPPORTUNITY_STATUS.INTAKE,
  OPPORTUNITY_STATUS.QUOTE_IN_PROGRESS,
  OPPORTUNITY_STATUS.SENT,
] as const;

export const MIN_OPPORTUNITY_PROBABILITY = 0;
export const MAX_OPPORTUNITY_PROBABILITY = 100;
export const DEFAULT_OPPORTUNITY_PROBABILITY = 25;
export const DEFAULT_OPPORTUNITY_NAMING_GUIDANCE = [
  "Prefix with the quote number if available",
  "Should be a concise part or project description of the RFQ in question",
  "If there are multiple parts, then it should be a summary of the parts in question",
  "Do not use customer name - this is already included as a field on the opportunity and quote",
  "Do not use email subject lines - this is not always descriptive / may be a one off",
].join("\n");

export const OpportunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  companyId: z.string().nullable(),
  salesRepUserId: z.string().nullable().optional(),
  estimatorUserIds: z.array(z.string()).default([]),
  locationId: z.string().nullable().optional(),
  amount: z.string(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY),
  status: OpportunityStatusSchema,
  stageId: z.string().nullable(),
  organizationId: z.string(),
  primaryThreadId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.nullable().optional(),
  firstQuoteSentAt: z.coerce.date().nullable(),
  firstQuoteInProgressAt: z.coerce.date().nullable(),
  closedAt: z.coerce.date().nullable(),
  archivedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Opportunity = z.infer<typeof OpportunitySchema>;

export const HydratedOpportunitySchema = OpportunitySchema.extend({
  company: HydratedCompanySchema.nullable().optional(),
  location: LocationSchema.nullable().optional(),
  /** People associated with this opportunity (contacts hydrated from the join table). */
  people: z.array(PersonSchema).optional(),
  /** Display names of human users who have pending-action tasks on this opportunity. */
  pendingActionOwnerNames: z.array(z.string()).optional(),
  /**
   * Raw quote numbers of this opportunity's non-archived quotes, distinct across
   * revisions and sorted ascending. Rendered as badges on the board card via the
   * org's quote-number prefix/pad settings. Absent when the serving endpoint did
   * not compute it.
   */
  quoteNumbers: z.array(z.number()).optional(),
  quoteCustomFields: z.array(CustomFieldValuesSchema).optional(),
  /**
   * Whether this opportunity's primary agent thread is actively running right
   * now — the "‹agent› is working" signal. Derived by the API from the thread
   * lifecycle (never stored); absent when the serving endpoint did not
   * compute it.
   */
  isPrimaryThreadWorking: z.boolean().optional(),
});
export type HydratedOpportunity = z.infer<typeof HydratedOpportunitySchema>;

export const CreateOpportunityParamsSchema = z.object({
  name: z.string(),
  companyId: z.string().optional(),
  amount: z.string(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .optional(),
  status: OpportunityStatusSchema.optional(),
  stageId: z.string().optional(),
  salesRepUserId: z.string().nullable().optional(),
  estimatorUserIds: z.array(z.string()).optional(),
  locationId: z.string().nullable().optional(),
  personIds: z.array(z.string()).optional(),
  customFields: CustomFieldValuesSchema.optional(),
});
export type CreateOpportunityParams = z.infer<
  typeof CreateOpportunityParamsSchema
>;

export const UpdateOpportunityParamsSchema = z.object({
  name: z.string().optional(),
  companyId: z.string().nullable().optional(),
  amount: z.string().optional(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .optional(),
  status: OpportunityStatusSchema.optional(),
  stageId: z.string().optional(),
  salesRepUserId: z.string().nullable().optional(),
  estimatorUserIds: z.array(z.string()).optional(),
  locationId: z.string().nullable().optional(),
  personIds: z.array(z.string()).optional(),
  customFields: CustomFieldValuesSchema.optional(),
});
export type UpdateOpportunityParams = z.infer<
  typeof UpdateOpportunityParamsSchema
>;

export const DEFAULT_OPPORTUNITY_LIST_LIMIT = 50;

/**
 * The filters available on the opportunity board: person roles, company,
 * location, custom field values, the created date range, and the archive view.
 * Shared by the frontend filter state and the list, statistics, and
 * people-filter-counts endpoints.
 */
export const OpportunityBoardFiltersSchema = z.object({
  salesRepUserIds: z.array(z.string()).optional(),
  estimatorUserIds: z.array(z.string()).optional(),
  taskAssigneeUserIds: z.array(z.string()).optional(),
  companyIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  customFields: CustomFieldFilterSchema.optional(),
  createdAfter: z.coerce.date().optional(),
  createdBefore: z.coerce.date().optional(),
  includeArchived: z.boolean().optional(),
  archivedOnly: z.boolean().optional(),
});
export type OpportunityBoardFilters = z.infer<
  typeof OpportunityBoardFiltersSchema
>;

export const OpportunityFilterSchema = z.object({
  search: z.string().optional(),
  statuses: z.array(OpportunityStatusSchema).optional(),
  stageIds: z.array(z.string()).optional(),
  salesRepUserIds: z.array(z.string()).optional(),
  estimatorUserIds: z.array(z.string()).optional(),
  taskAssigneeUserIds: z.array(z.string()).optional(),
  companyIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  customFields: CustomFieldFilterSchema.optional(),
  createdAfter: z.coerce.date().optional(),
  createdBefore: z.coerce.date().optional(),
  updatedAfter: z.coerce.date().optional(),
  updatedBefore: z.coerce.date().optional(),
  firstQuoteSentAfter: z.coerce.date().optional(),
  firstQuoteSentBefore: z.coerce.date().optional(),
  firstQuoteInProgressAfter: z.coerce.date().optional(),
  firstQuoteInProgressBefore: z.coerce.date().optional(),
  closedAfter: z.coerce.date().optional(),
  closedBefore: z.coerce.date().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
  includeArchived: z.boolean().optional(),
  archivedOnly: z.boolean().optional(),
  companyId: z.string().optional(),
  personId: z.string().optional(),
  /**
   * Restricts to opportunities whose primaryThreadId is in this set. An empty
   * array matches nothing. Backs the list endpoint's
   * `isPrimaryAgentThreadWorking=true` query param, with the working thread
   * ids supplied by the agent SDK module.
   */
  primaryThreadIdIn: z.array(z.string()).optional(),
});
export type OpportunityFilter = z.infer<typeof OpportunityFilterSchema>;

export const ListOpportunitiesResponseSchema = z.object({
  opportunities: z.array(HydratedOpportunitySchema),
  total: z.number(),
});
export type ListOpportunitiesResponse = z.infer<
  typeof ListOpportunitiesResponseSchema
>;

export const GetOpportunityResponseSchema = z.object({
  opportunity: HydratedOpportunitySchema,
});
export type GetOpportunityResponse = z.infer<
  typeof GetOpportunityResponseSchema
>;

export const CreateOpportunityResponseSchema = z.object({
  opportunity: HydratedOpportunitySchema,
});
export type CreateOpportunityResponse = z.infer<
  typeof CreateOpportunityResponseSchema
>;

export const UpdateOpportunityResponseSchema = z.object({
  opportunity: HydratedOpportunitySchema,
});
export type UpdateOpportunityResponse = z.infer<
  typeof UpdateOpportunityResponseSchema
>;

export const OpportunityStatisticsSchema = z.object({
  amountInProgress: z.string(),
  weightedAmountInProgress: z.string(),
  acceptedCount: z.number(),
  closedCount: z.number(),
});
export type OpportunityStatistics = z.infer<typeof OpportunityStatisticsSchema>;

export const GetOpportunityStatisticsResponseSchema = z.object({
  statistics: OpportunityStatisticsSchema,
});
export type GetOpportunityStatisticsResponse = z.infer<
  typeof GetOpportunityStatisticsResponseSchema
>;

/**
 * Per-person opportunity counts for each board person-filter, keyed by user id.
 * Lets the filter UI show how many opportunities each person owns in each role.
 */
export const OpportunityPeopleFilterCountsSchema = z.object({
  salesRepUserIds: z.record(z.string(), z.number()),
  estimatorUserIds: z.record(z.string(), z.number()),
  taskAssigneeUserIds: z.record(z.string(), z.number()),
});
export type OpportunityPeopleFilterCounts = z.infer<
  typeof OpportunityPeopleFilterCountsSchema
>;

export const GetOpportunityPeopleFilterCountsResponseSchema = z.object({
  filterCounts: OpportunityPeopleFilterCountsSchema,
});
export type GetOpportunityPeopleFilterCountsResponse = z.infer<
  typeof GetOpportunityPeopleFilterCountsResponseSchema
>;

export const DEFAULT_OPPORTUNITY_COMPANY_FILTER_OPTIONS_LIMIT = 50;
export const MAX_OPPORTUNITY_COMPANY_FILTER_OPTIONS_LIMIT = 200;

/**
 * Query params for the board's company-filter options: an optional name search
 * and the page size.
 */
export const OpportunityCompanyFilterOptionsQuerySchema = z.object({
  search: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(MAX_OPPORTUNITY_COMPANY_FILTER_OPTIONS_LIMIT)
    .default(DEFAULT_OPPORTUNITY_COMPANY_FILTER_OPTIONS_LIMIT),
});
export type OpportunityCompanyFilterOptionsQuery = z.infer<
  typeof OpportunityCompanyFilterOptionsQuerySchema
>;

/**
 * The companies selectable in the board's company filter: those attached to
 * opportunities within the caller's opportunity read scope.
 */
export const GetOpportunityCompanyFilterOptionsResponseSchema = z.object({
  companies: z.array(HydratedCompanySchema),
});
export type GetOpportunityCompanyFilterOptionsResponse = z.infer<
  typeof GetOpportunityCompanyFilterOptionsResponseSchema
>;

export const OpportunitySettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  defaultProbability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
  autoCloseAfterDays: z.number().int().positive().nullable(),
  autoCloseTargetStageId: z.string().nullable(),
  opportunityNamingGuidance: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type OpportunitySettings = z.infer<typeof OpportunitySettingsSchema>;

export const UpdateOpportunitySettingsParamsSchema = z.object({
  defaultProbability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .optional(),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable().optional(),
  autoCloseAfterDays: z.number().int().positive().nullable().optional(),
  autoCloseTargetStageId: z.string().nullable().optional(),
  opportunityNamingGuidance: z.string().nullable().optional(),
});
export type UpdateOpportunitySettingsParams = z.infer<
  typeof UpdateOpportunitySettingsParamsSchema
>;

export const GetOpportunitySettingsResponseSchema = z.object({
  settings: OpportunitySettingsSchema.nullable(),
});
export type GetOpportunitySettingsResponse = z.infer<
  typeof GetOpportunitySettingsResponseSchema
>;

export const UpsertOpportunitySettingsResponseSchema = z.object({
  settings: OpportunitySettingsSchema,
});
export type UpsertOpportunitySettingsResponse = z.infer<
  typeof UpsertOpportunitySettingsResponseSchema
>;
